import React from 'react'
import { Box, DatePicker, Field } from '@workwhile/ui'
import { Controller, useFormContext, type Path } from 'react-hook-form'
import { formatDateRange } from 'lib/formatDateRange'
import { DateRangeFilter, type DateRangeFilterProps } from './DateRangeFilter'

export { formatDateRange } from 'lib/formatDateRange'

type DateRangePickerInputsProps<FilterSchema extends Record<string, any>> = {
  fieldPath: Path<FilterSchema>
  placeholder?: string
}

const DateRangePickerInputs = <FilterSchema extends Record<string, any>>({
  fieldPath,
  placeholder = 'Select date range',
}: DateRangePickerInputsProps<FilterSchema>) => {
  const { control } = useFormContext<FilterSchema>()

  return (
    <Box>
      <Controller
        name={fieldPath}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Field error={error?.message?.toString()}>
            <DatePicker
              mode="range"
              selected={field.value as { from: Date; to: Date }}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore — DatePicker ref typing does not match RHF field.ref
              ref={field.ref}
              onSelect={(days) => {
                field.onChange(days)
              }}
              placeholder={placeholder}
            />
          </Field>
        )}
      />
    </Box>
  )
}

export type DateRangePickerFilterProps<
  FilterSchema extends Record<string, any>,
> = Pick<
  DateRangeFilterProps<FilterSchema>,
  | 'dateRangeFieldPath'
  | 'label'
  | 'filterKey'
  | 'formatSelectedAttributes'
  | 'resetDefaultValues'
  | 'typeFieldPath'
> & {
  /** Placeholder text on the range picker (default: "Select date range"). */
  placeholder?: string
}

/**
 * {@link DateRangeFilter} plus the standard range {@link DatePicker} wired to react-hook-form.
 * Use this instead of re-implementing TimeRangeInputs on each filters form.
 */
export const DateRangePickerFilter = <
  FilterSchema extends Record<string, any>,
>({
  dateRangeFieldPath,
  label,
  filterKey,
  formatSelectedAttributes = formatDateRange,
  placeholder,
  resetDefaultValues,
  typeFieldPath,
}: DateRangePickerFilterProps<FilterSchema>) => {
  return (
    <DateRangeFilter<FilterSchema>
      dateRangeFieldPath={dateRangeFieldPath}
      typeFieldPath={typeFieldPath}
      label={label}
      filterKey={filterKey}
      formatSelectedAttributes={formatSelectedAttributes}
      resetDefaultValues={resetDefaultValues}
    >
      <DateRangePickerInputs<FilterSchema>
        fieldPath={dateRangeFieldPath}
        placeholder={placeholder}
      />
    </DateRangeFilter>
  )
}
